import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useT } from '../i18n/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import EmptyState from '../components/ui/EmptyState';
import SearchRow, { SearchInput } from '../components/ui/SearchRow';
import Select from '../components/ui/Select';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import DocumentFormModal from '../components/documents/DocumentFormModal';
import DocumentViewModal from '../components/documents/DocumentViewModal';
import { CATEGORY_ICON, CATEGORY_TONE, DOCUMENT_CATEGORIES, htmlExcerpt } from '../components/documents/constants';
import { useApi } from '../api/client';
import { createDocument, deleteDocument, getDocument, listDocuments, updateDocument } from '../api/documents';
import useDebounce from '../hooks/useDebounce';
import { formatDate } from '../utils/format';

export default function DocumentsPage() {
  const t = useT();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('documents:create');
  const canUpdate = hasPermission('documents:update');
  const canDelete = hasPermission('documents:delete');

  const [docs, setDocs] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [size] = useState(24);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const debouncedQuery = useDebounce(query, 250);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    if (!useApi) return;
    setLoading(true);
    try {
      const data = await listDocuments({ q: debouncedQuery, category, page, size });
      setDocs(data.content);
      setTotal(data.totalElements || 0);
      setTotalPages(data.totalPages || 0);
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, category, page, size, toast, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [debouncedQuery, category]);

  const categoryOptions = useMemo(
    () => [
      { value: '', label: t('documents_page.filter_category') },
      ...DOCUMENT_CATEGORIES.map((c) => ({ value: c, label: t(`documents_page.categories.${c}`) })),
    ],
    [t]
  );

  if (!useApi) {
    return (
      <Card title={t('pages.documents.title')} subtitle={t('pages.documents.subtitle')}>
        <EmptyState icon="🔌" title={t('common.api_required')} />
      </Card>
    );
  }

  const openNew = () => { setEditing(null); setFormOpen(true); };

  // The list may be trimmed by the backend; fetch the full record to be safe.
  const openView = async (doc) => {
    setViewing(doc);
    try {
      const fresh = await getDocument(doc.id);
      setViewing((prev) => (prev && prev.id === fresh.id ? fresh : prev));
    } catch {
      // Keep the list copy if the refresh fails.
    }
  };

  const handleSubmit = async (data) => {
    try {
      if (editing) {
        const saved = await updateDocument(editing.id, data);
        setDocs((prev) => prev.map((d) => (d.id === saved.id ? saved : d)));
        setViewing((prev) => (prev && prev.id === saved.id ? saved : prev));
        toast.success(t('documents_page.toast_updated', { title: saved.title }));
      } else {
        const saved = await createDocument(data);
        setDocs((prev) => [saved, ...prev]);
        setTotal((n) => n + 1);
        toast.success(t('documents_page.toast_created', { title: saved.title }));
      }
      setFormOpen(false);
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteDocument(deleting.id);
      setDocs((prev) => prev.filter((d) => d.id !== deleting.id));
      setTotal((n) => Math.max(0, n - 1));
      setViewing(null);
      toast.success(t('documents_page.toast_deleted'));
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div>
      <SearchRow>
        <SearchInput value={query} onChange={setQuery} placeholder={t('documents_page.search_placeholder')} />
        <Select value={category} onChange={(e) => setCategory(e.target.value)} options={categoryOptions} className="filter-select" />
        {canCreate && <Button variant="gold" onClick={openNew}>+ {t('documents_page.new_document')}</Button>}
      </SearchRow>

      {docs.length === 0 ? (
        <Card title={t('documents_page.title')} subtitle={t('documents_page.subtitle')}>
          <EmptyState
            icon="📜"
            title={loading ? t('common.loading') : t('documents_page.empty_title')}
            body={loading ? null : t('documents_page.empty_body')}
            action={!loading && canCreate ? <Button variant="gold" onClick={openNew}>+ {t('documents_page.new_document')}</Button> : null}
          />
        </Card>
      ) : (
        <>
          <div className="doc-grid">
            {docs.map((d) => (
              <button key={d.id} type="button" className="doc-card" onClick={() => openView(d)}>
                <div className="doc-card__top">
                  <span className="doc-card__icon" aria-hidden>{CATEGORY_ICON[d.category] || '📄'}</span>
                  <Badge tone={CATEGORY_TONE[d.category]}>{t(`documents_page.categories.${d.category}`)}</Badge>
                </div>
                <div className="doc-card__title">{d.title}</div>
                <div className="doc-card__meta">
                  {[d.meetingDate && formatDate(d.meetingDate, t), d.location].filter(Boolean).join(' · ') || '—'}
                </div>
                <p className="doc-card__excerpt">{htmlExcerpt(d.contentHtml) || t('documents_page.no_content')}</p>
                <div className="doc-card__foot">
                  <span>{d.authorName || '—'}</span>
                  <span className="mono">{d.code}</span>
                </div>
              </button>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="pagination pagination--standalone">
              <div className="results-meta">
                {t('common.showing_results', {
                  from: page * size + 1,
                  to: Math.min(total, (page + 1) * size),
                  total,
                })}
              </div>
              <div className="pagination-controls">
                <Button size="sm" variant="ghost" disabled={page === 0 || loading} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                  {t('common.previous')}
                </Button>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {t('common.page_of', { current: page + 1, total: Math.max(1, totalPages) })}
                </span>
                <Button size="sm" variant="ghost" disabled={page >= totalPages - 1 || loading} onClick={() => setPage((p) => p + 1)}>
                  {t('common.next')}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <DocumentFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
        initial={editing}
      />

      <DocumentViewModal
        isOpen={Boolean(viewing) && !formOpen && !deleting}
        onClose={() => setViewing(null)}
        doc={viewing}
        onEdit={canUpdate ? () => { setEditing(viewing); setFormOpen(true); } : undefined}
        onDelete={canDelete ? () => setDeleting(viewing) : undefined}
      />

      <ConfirmDialog
        isOpen={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={t('documents_page.delete_confirm_title')}
        body={deleting ? t('documents_page.delete_confirm_body', { title: deleting.title }) : ''}
        confirmLabel={t('common.delete')}
      />
    </div>
  );
}
